import { flattenGroups } from './merge_tags';
import { ICONS } from './icons';
import { openImageCropper } from './image_crop';

const FONT_FAMILIES = [
  ['Padrão', ''],
  ['Arial', 'Arial, sans-serif'],
  ['Times New Roman', "'Times New Roman', serif"],
  ['Georgia', 'Georgia, serif'],
  ['Courier New', "'Courier New', monospace"],
  ['Verdana', 'Verdana, sans-serif']
];

const FONT_SIZES = ['8pt', '10pt', '12pt', '14pt', '16pt', '18pt', '24pt', '36pt', '48pt'];

const ZOOM_MIN = 50;
const ZOOM_MAX = 200;
const ZOOM_STEP = 10;

// ---- Small DOM helpers -----------------------------------------------------

export function element(tag, className, html) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (html != null) node.innerHTML = html;
  return node;
}

function divider() {
  return element('span', 'tr-editor__divider');
}

// Icon button. `isActive` (optional) is a predicate re-run on every editor change
// to toggle the `is-active` highlight, and is collected into `updaters`.
export function iconButton(svg, title, onClick, updaters, isActive) {
  const button = element('button', 'tr-editor__btn', svg);
  button.type = 'button';
  button.title = title;
  button.addEventListener('click', (event) => {
    event.preventDefault();
    onClick();
  });
  if (isActive) {
    updaters.push(() => button.classList.toggle('is-active', isActive()));
  }
  return button;
}

function selectControl(options, onChange, ariaLabel) {
  const select = element('select', 'tr-editor__select');
  select.setAttribute('aria-label', ariaLabel);
  options.forEach(([label, value]) => {
    const option = element('option', null);
    option.textContent = label;
    option.value = value;
    select.appendChild(option);
  });
  select.addEventListener('change', () => onChange(select.value));
  return select;
}

// Generic popover: a trigger button + a hidden menu, toggled on click and closed
// on outside click. Returns the wrapper plus the menu node to fill in.
function popover(triggerHTML, title, extraClass) {
  const wrapper = element('div', `tr-editor__pop${extraClass ? ` ${extraClass}` : ''}`);
  const trigger = element('button', 'tr-editor__btn tr-editor__pop-trigger', triggerHTML);
  trigger.type = 'button';
  trigger.title = title;
  const menu = element('div', 'tr-editor__pop-menu');

  const close = () => wrapper.classList.remove('is-open');
  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    wrapper.classList.toggle('is-open');
  });
  document.addEventListener('click', (clickEvent) => {
    if (!wrapper.contains(clickEvent.target)) close();
  });

  wrapper.appendChild(trigger);
  wrapper.appendChild(menu);
  return { wrapper, trigger, menu, close };
}

// Inserts a picked image. When an `onUploadImage` handler is provided (editors that opt
// into upload, e.g. announcements) the file is uploaded and the returned URL is inserted,
// keeping the body small. Otherwise it falls back to the legacy base64 data-URI inline
// (contracts/documents rely on the self-contained HTML for WickedPDF).
export function insertImageFromFile(editor, onUploadImage) {
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.addEventListener('change', () => {
    const file = fileInput.files && fileInput.files[0];
    if (!file) return;
    insertImage(editor, file, onUploadImage);
  });
  fileInput.click();
}

// Shared insertion path used by the toolbar button and by paste/drop: upload when a
// handler exists, otherwise embed as base64.
export function insertImage(editor, file, onUploadImage) {
  if (onUploadImage) {
    onUploadImage(file)
      .then((url) => editor.chain().focus().setImage({ src: url }).run())
      .catch((error) => {
        console.error(error);
        window.alert('Falha ao enviar a imagem. Tente novamente.');
      });
    return;
  }
  const reader = new FileReader();
  reader.onload = () => editor.chain().focus().setImage({ src: reader.result }).run();
  reader.readAsDataURL(file);
}

// ---- Compound controls -----------------------------------------------------

// Zoom cluster: − [NN%] + — scales the editor surface only (view-only, never the
// saved HTML). Matches the leftmost control in the reference layout.
function zoomCluster(editor) {
  const cluster = element('div', 'tr-editor__zoom');
  let level = 100;
  const label = element('span', 'tr-editor__zoom-label', '100%');

  const apply = () => {
    label.textContent = `${level}%`;
    editor.view.dom.style.zoom = `${level / 100}`;
  };
  const step = (delta) => {
    level = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, level + delta));
    apply();
  };

  const out = iconButton(ICONS.minus, 'Reduzir zoom', () => step(-ZOOM_STEP), []);
  const inn = iconButton(ICONS.plus, 'Aumentar zoom', () => step(ZOOM_STEP), []);
  cluster.appendChild(out);
  cluster.appendChild(label);
  cluster.appendChild(inn);
  return cluster;
}

// Text color: an "A ▾" trigger opening a native color picker, with a swatch bar
// reflecting the current color.
export function colorControl(editor) {
  const wrapper = element('div', 'tr-editor__colorbtn');
  const button = element('button', 'tr-editor__btn', `<span class="tr-editor__a">A</span>${ICONS.caretDown}`);
  button.type = 'button';
  button.title = 'Cor do texto';
  const swatch = element('span', 'tr-editor__colorbar');

  const input = document.createElement('input');
  input.type = 'color';
  input.className = 'tr-editor__colorinput';
  input.value = '#1f2937';
  swatch.style.background = input.value;

  button.addEventListener('click', (event) => {
    event.preventDefault();
    input.click();
  });
  input.addEventListener('input', () => {
    swatch.style.background = input.value;
    editor.chain().focus().setColor(input.value).run();
  });

  button.appendChild(swatch);
  wrapper.appendChild(button);
  wrapper.appendChild(input);
  return wrapper;
}

// Alignment popover: a single trigger (showing the active alignment icon) opening
// left / center / right / justify options.
function alignControl(editor, updaters) {
  const ALIGNMENTS = [
    ['left', ICONS.alignLeft, 'Alinhar à esquerda'],
    ['center', ICONS.alignCenter, 'Centralizar'],
    ['right', ICONS.alignRight, 'Alinhar à direita'],
    ['justify', ICONS.alignJustify, 'Justificar']
  ];
  const pop = popover(`${ICONS.alignLeft}${ICONS.caretDown}`, 'Alinhamento');

  ALIGNMENTS.forEach(([value, svg, title]) => {
    const option = element('button', 'tr-editor__pop-item', `${svg}<span>${title}</span>`);
    option.type = 'button';
    option.addEventListener('click', (clickEvent) => {
      clickEvent.preventDefault();
      editor.chain().focus().setTextAlign(value).run();
      pop.close();
    });
    pop.menu.appendChild(option);
  });

  // Trigger mirrors the current alignment icon.
  updaters.push(() => {
    const current = ALIGNMENTS.find(([value]) => editor.isActive({ textAlign: value }));
    pop.trigger.innerHTML = `${current ? current[1] : ICONS.alignLeft}${ICONS.caretDown}`;
  });
  return pop.wrapper;
}

// Indent popover: increase / decrease list nesting (the standard StarterKit indent).
function indentControl(editor) {
  const pop = popover(`${ICONS.indent}${ICONS.caretDown}`, 'Recuo');
  const options = [
    [ICONS.indent, 'Aumentar recuo', () => editor.chain().focus().sinkListItem('listItem').run()],
    [ICONS.outdent, 'Diminuir recuo', () => editor.chain().focus().liftListItem('listItem').run()]
  ];
  options.forEach(([svg, title, run]) => {
    const option = element('button', 'tr-editor__pop-item', `${svg}<span>${title}</span>`);
    option.type = 'button';
    option.addEventListener('click', (clickEvent) => {
      clickEvent.preventDefault();
      run();
      pop.close();
    });
    pop.menu.appendChild(option);
  });
  return pop.wrapper;
}

// "Variáveis" popover with a live search box, grouped by dictionary title. Selecting
// a variable inserts its "{{value}}" token. Complements the inline "{{" autocomplete.
function mergeTagControl(editor, groups) {
  const allItems = flattenGroups(groups);
  const pop = popover(
    `<span class="tr-editor__pop-label">Variáveis</span>${ICONS.caretDown}`,
    'Inserir variável',
    'tr-editor__pop--wide'
  );
  pop.trigger.classList.add('tr-editor__btn--accent');

  const search = element('div', 'tr-editor__search', ICONS.search);
  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.placeholder = 'Buscar variável…';
  searchInput.addEventListener('click', (event) => event.stopPropagation());
  search.appendChild(searchInput);

  const results = element('div', 'merge-tag-menu');

  const insert = (item) => {
    editor.chain().focus().insertContent(`{{${item.value}}} `).run();
    pop.close();
  };

  const matching = () => {
    const term = searchInput.value.trim().toLowerCase();
    if (!term) return allItems;
    return allItems.filter(
      (item) => item.title.toLowerCase().includes(term) || item.value.toLowerCase().includes(term)
    );
  };

  const renderResults = () => {
    const matches = matching();
    results.innerHTML = '';
    if (matches.length === 0) {
      results.appendChild(element('div', 'merge-tag-menu__empty', 'Nenhuma variável encontrada'));
      return;
    }
    let lastGroupTitle = null;
    matches.forEach((item) => {
      if (item.group && item.group !== lastGroupTitle) {
        results.appendChild(element('div', 'merge-tag-menu__group', item.group));
        lastGroupTitle = item.group;
      }
      const itemButton = element(
        'button',
        'merge-tag-menu__item',
        '<span class="merge-tag-menu__title"></span><span class="merge-tag-menu__code"></span>'
      );
      itemButton.type = 'button';
      itemButton.querySelector('.merge-tag-menu__title').textContent = item.title;
      itemButton.querySelector('.merge-tag-menu__code').textContent = `{{${item.value}}}`;
      itemButton.addEventListener('click', (clickEvent) => {
        clickEvent.preventDefault();
        insert(item);
      });
      results.appendChild(itemButton);
    });
  };

  searchInput.addEventListener('input', renderResults);
  searchInput.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const matches = matching();
    if (matches.length === 1) {
      event.preventDefault();
      insert(matches[0]);
    }
  });

  pop.trigger.addEventListener('click', () => {
    if (!pop.wrapper.classList.contains('is-open')) return;
    searchInput.value = '';
    renderResults();
    window.requestAnimationFrame(() => searchInput.focus());
  });

  pop.menu.appendChild(search);
  pop.menu.appendChild(results);
  renderResults();
  return pop.wrapper;
}

// Source-code view: swaps the rich surface for a raw-HTML <textarea>. Opening loads
// the current HTML (editor.getHTML); closing applies the edited HTML back via
// setContent with emitUpdate=true, so the hidden form textarea and the merge-tag
// decorations refresh. Mirrors the legacy TinyMCE "Código-fonte" dialog, inline.
function sourceControl(editor) {
  const host = editor.view.dom.closest('.tr-editor');
  const source = element('textarea', 'tr-editor__source');
  source.spellcheck = false;
  source.style.display = 'none';
  host.appendChild(source);

  const button = element('button', 'tr-editor__btn tr-editor__source-toggle', ICONS.code);
  button.type = 'button';
  button.title = 'Código-fonte';

  const open = () => {
    source.value = editor.getHTML();
    editor.view.dom.style.display = 'none';
    source.style.display = 'block';
    host.classList.add('is-source');
    button.classList.add('is-active');
    source.focus();
  };
  const close = () => {
    editor.commands.setContent(source.value, true);
    source.style.display = 'none';
    editor.view.dom.style.display = '';
    host.classList.remove('is-source');
    button.classList.remove('is-active');
    editor.commands.focus();
  };

  button.addEventListener('click', (event) => {
    event.preventDefault();
    if (host.classList.contains('is-source')) close();
    else open();
  });
  return button;
}

// Toggles a view-only dark surface on the editor (never affects the saved HTML).
function themeControl(editor) {
  return iconButton(
    ICONS.sun,
    'Alternar tema do editor',
    () => {
      const surface = editor.view.dom.closest('.tr-editor');
      if (surface) surface.classList.toggle('is-dark');
    },
    []
  );
}

// ---- Public builder --------------------------------------------------------

// Builds the toolbar DOM wired to a Tiptap editor and registers active-state
// refreshers on every editor change.
//   opts.groups    → dictionary groups; when present the "Variáveis" control appears
//   opts.onPreview → optional callback for the "Pré-Visualizar Contrato" button
export function buildToolbar(editor, opts = {}) {
  const toolbar = element('div', 'tr-editor__toolbar');
  const updaters = [];

  // Caller-supplied text buttons (e.g. the commission "Validar Expressão").
  const appendExtras = () => {
    (opts.extraButtons || []).forEach((spec) => {
      const button = iconButton(spec.label, spec.title || spec.label, spec.onClick, []);
      button.classList.add('tr-editor__btn--text');
      if (spec.accent) button.classList.add('tr-editor__btn--accent');
      toolbar.appendChild(button);
    });
  };

  // Minimal variant: only merge tags + custom buttons, no rich-text chrome. Used by
  // the commission expression editor.
  if (opts.variant === 'minimal') {
    if (opts.groups && opts.groups.length) toolbar.appendChild(mergeTagControl(editor, opts.groups));
    appendExtras();
    return toolbar;
  }

  toolbar.appendChild(zoomCluster(editor));
  toolbar.appendChild(divider());

  const headingSelect = selectControl(
    [['Parágrafo', 'paragraph'], ['Título 1', '1'], ['Título 2', '2'], ['Título 3', '3']],
    (value) => {
      if (value === 'paragraph') editor.chain().focus().setParagraph().run();
      else editor.chain().focus().setHeading({ level: Number(value) }).run();
    },
    'Estilo de bloco'
  );
  updaters.push(() => {
    const level = [1, 2, 3].find((candidate) => editor.isActive('heading', { level: candidate }));
    headingSelect.value = level ? String(level) : 'paragraph';
  });
  toolbar.appendChild(headingSelect);

  const fontSelect = selectControl(
    [['Fonte', ''], ...FONT_FAMILIES.slice(1)],
    (value) => {
      if (value) editor.chain().focus().setFontFamily(value).run();
      else editor.chain().focus().unsetFontFamily().run();
    },
    'Fonte'
  );
  updaters.push(() => {
    fontSelect.value = editor.getAttributes('textStyle').fontFamily || '';
  });
  toolbar.appendChild(fontSelect);

  const sizeSelect = selectControl(
    [['Tamanho', ''], ...FONT_SIZES.map((size) => [size, size])],
    (value) => {
      if (value) editor.chain().focus().setFontSize(value).run();
      else editor.chain().focus().unsetFontSize().run();
    },
    'Tamanho da fonte'
  );
  updaters.push(() => {
    sizeSelect.value = editor.getAttributes('textStyle').fontSize || '';
  });
  toolbar.appendChild(sizeSelect);
  toolbar.appendChild(divider());

  toolbar.appendChild(iconButton(ICONS.bold, 'Negrito', () => editor.chain().focus().toggleBold().run(), updaters, () => editor.isActive('bold')));
  toolbar.appendChild(iconButton(ICONS.italic, 'Itálico', () => editor.chain().focus().toggleItalic().run(), updaters, () => editor.isActive('italic')));
  toolbar.appendChild(iconButton(ICONS.strikethrough, 'Tachado', () => editor.chain().focus().toggleStrike().run(), updaters, () => editor.isActive('strike')));
  toolbar.appendChild(sourceControl(editor));
  toolbar.appendChild(colorControl(editor));
  toolbar.appendChild(divider());

  toolbar.appendChild(
    iconButton(ICONS.link, 'Inserir link', () => {
      const url = window.prompt('URL do link:');
      if (url) editor.chain().focus().setLink({ href: url }).run();
      else editor.chain().focus().unsetLink().run();
    }, updaters, () => editor.isActive('link'))
  );
  toolbar.appendChild(iconButton(ICONS.image, 'Inserir imagem', () => insertImageFromFile(editor, opts.onUploadImage), []));

  // Crop the selected image (resize + align come from ImageResize itself). Disabled
  // until an image node is selected.
  const cropButton = iconButton(ICONS.crop, 'Cortar imagem', () => openImageCropper(editor), []);
  updaters.push(() => { cropButton.disabled = !editor.isActive('imageResize'); });
  toolbar.appendChild(cropButton);
  toolbar.appendChild(divider());

  toolbar.appendChild(iconButton(ICONS.listBullets, 'Lista com marcadores', () => editor.chain().focus().toggleBulletList().run(), updaters, () => editor.isActive('bulletList')));
  toolbar.appendChild(iconButton(ICONS.listNumbers, 'Lista numerada', () => editor.chain().focus().toggleOrderedList().run(), updaters, () => editor.isActive('orderedList')));
  toolbar.appendChild(alignControl(editor, updaters));
  toolbar.appendChild(indentControl(editor));
  toolbar.appendChild(
    iconButton(ICONS.pageBreak, 'Quebra de página', () => editor.chain().focus().setPageBreak().run(), [])
  );

  // Domain controls pushed to the right, matching the reference layout's far side.
  toolbar.appendChild(element('span', 'tr-editor__spacer'));
  if (opts.groups && opts.groups.length) toolbar.appendChild(mergeTagControl(editor, opts.groups));
  if (typeof opts.onPreview === 'function') {
    toolbar.appendChild(iconButton(ICONS.filePdf, 'Pré-Visualizar Contrato', opts.onPreview, []));
  }
  appendExtras();
  toolbar.appendChild(themeControl(editor));

  const refresh = () => updaters.forEach((update) => update());
  editor.on('transaction', refresh);
  refresh();

  return toolbar;
}
